import { Button } from '@ophelos/ui/components/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@ophelos/ui/components/dialog';
import { Input } from '@ophelos/ui/components/input';
import { Label } from '@ophelos/ui/components/label';
import { Slider } from '@ophelos/ui/components/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ophelos/ui/components/tabs';
import { Upload, X, ZoomIn, ZoomOut } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { Area } from 'react-easy-crop';
import Cropper from 'react-easy-crop';

const MAX_IMAGE_KB = 100;
const MAX_IMAGE_BYTES = MAX_IMAGE_KB * 1024;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface ImageUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentUrl?: string;
  onSave: (url: string | null) => void;
  accept?: string;
  description?: string;
  /** Enable the cropper after image selection. Defaults to true. */
  crop?: boolean;
  cropAspect?: number;
  cropShape?: 'rect' | 'round';
}

/**
 * Creates a cropped image from a source image and crop area.
 * Returns a data URL of the cropped result.
 */
function getCroppedImageDataUrl(imageSrc: string, cropArea: Area): Promise<string> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = cropArea.width;
      canvas.height = cropArea.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      ctx.drawImage(image, cropArea.x, cropArea.y, cropArea.width, cropArea.height, 0, 0, cropArea.width, cropArea.height);
      resolve(canvas.toDataURL('image/png'));
    };
    image.onerror = () => reject(new Error('Failed to load image'));
    image.crossOrigin = 'anonymous';
    image.src = imageSrc;
  });
}

export function ImageUploadDialog({
  open,
  onOpenChange,
  currentUrl,
  onSave,
  accept = 'image/*',
  description = 'Upload an image file or provide a URL',
  crop: cropEnabled = true,
  cropAspect = 1,
  cropShape = 'round',
}: ImageUploadDialogProps) {
  const [uploadMode, setUploadMode] = useState<'upload' | 'url'>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [urlInput, setUrlInput] = useState(currentUrl || '');
  const [isDragging, setIsDragging] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);

  // Cropper state
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const resetCropState = useCallback(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  }, []);

  // Seed from the current value whenever the dialog opens. Callers open it by setting
  // `open` directly, which never routes through onOpenChange, so seeding there would
  // leave the dialog showing whatever the value was one save ago.
  useEffect(() => {
    if (!open) return;
    setUrlInput(currentUrl || '');
    setImageSrc(currentUrl || null);
    setUploadMode(currentUrl ? 'url' : 'upload');
    setSelectedFile(null);
    setSizeError(null);
    resetCropState();
  }, [open, currentUrl, resetCropState]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      onOpenChange(newOpen);
      if (!newOpen) {
        setSelectedFile(null);
        setImageSrc(null);
        setIsDragging(false);
        setSizeError(null);
        resetCropState();
      }
    },
    [onOpenChange, resetCropState]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const loadFileAsDataUrl = useCallback((file: File) => {
    // The image is inlined into the theme config as a data URI, which is then merged into
    // every compiled-config read and served on each journey page load — so an oversized
    // file is a payload problem for the customer portal, not just this dialog.
    if (file.size > MAX_IMAGE_BYTES) {
      setSizeError(`${formatFileSize(file.size)} exceeds the ${MAX_IMAGE_KB} KB limit. Use a smaller image, or link one by URL.`);
      setSelectedFile(null);
      setImageSrc(null);
      return;
    }
    setSizeError(null);
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      setImageSrc(event.target?.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0 && files[0].type.startsWith('image/')) {
        resetCropState();
        loadFileAsDataUrl(files[0]);
      }
    },
    [loadFileAsDataUrl, resetCropState]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0 && files[0].type.startsWith('image/')) {
        resetCropState();
        loadFileAsDataUrl(files[0]);
      }
    },
    [loadFileAsDataUrl, resetCropState]
  );

  const handleUrlChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = e.target.value;
    setUrlInput(newUrl);
    if (newUrl) {
      try {
        new URL(newUrl);
        setImageSrc(newUrl);
      } catch {
        setImageSrc(null);
      }
    } else {
      setImageSrc(null);
    }
  }, []);

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = useCallback(async () => {
    if (!imageSrc) {
      onSave(null);
      handleOpenChange(false);
      return;
    }

    // URL mode: pass through directly (can't crop cross-origin images)
    if (uploadMode === 'url') {
      onSave(imageSrc);
      handleOpenChange(false);
      return;
    }

    // File upload with cropping
    if (cropEnabled && croppedAreaPixels) {
      try {
        const croppedDataUrl = await getCroppedImageDataUrl(imageSrc, croppedAreaPixels);
        onSave(croppedDataUrl);
        handleOpenChange(false);
      } catch (err) {
        console.error('Error cropping image:', err);
      }
    } else if (!cropEnabled && selectedFile) {
      // No cropping — pass through the raw data URL
      const reader = new FileReader();
      reader.onload = (event) => {
        onSave(event.target?.result as string);
        handleOpenChange(false);
      };
      reader.readAsDataURL(selectedFile);
    }
  }, [imageSrc, croppedAreaPixels, cropEnabled, uploadMode, selectedFile, onSave, handleOpenChange]);

  const handleRemove = useCallback(() => {
    setSelectedFile(null);
    setImageSrc(null);
    setSizeError(null);
    resetCropState();
  }, [resetCropState]);

  const canCrop = cropEnabled && uploadMode === 'upload';
  const showCropper = canCrop && !!imageSrc;
  const showPreview = !canCrop && !!imageSrc;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upload Image</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <Tabs value={uploadMode} onValueChange={(v) => setUploadMode(v as typeof uploadMode)} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload">Upload</TabsTrigger>
            <TabsTrigger value="url">URL</TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="space-y-4 mt-4">
            {!selectedFile ? (
              <button
                type="button"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`
                  w-full border bg-soft border-border-heavy border-dashed rounded-lg p-8 text-center transition-colors
                  ${isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'}
                  hover:bg-muted cursor-pointer
                `}
                onClick={() => document.getElementById('image-upload')?.click()}
              >
                <input type="file" id="image-upload" className="hidden" onChange={handleFileInput} accept={accept} />
                <Upload className="size-10 mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm font-medium mb-1">Drop your image here, or click to browse</p>
                <p className="text-xs text-muted-foreground">Supports JPG, PNG, SVG, GIF, WebP ({MAX_IMAGE_KB} KB max)</p>
              </button>
            ) : (
              <div className="space-y-4">
                <div className="border rounded-lg p-4 flex items-center justify-between bg-muted/30">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{selectedFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(selectedFile.size)} • {selectedFile.type || 'Unknown type'}
                    </p>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={handleRemove} className="ml-2">
                    <X className="size-4" />
                  </Button>
                </div>
              </div>
            )}
            {sizeError && <p className="text-xs text-destructive">{sizeError}</p>}
          </TabsContent>

          <TabsContent value="url" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="image-url">Image URL</Label>
              <Input
                id="image-url"
                type="url"
                value={urlInput}
                onChange={handleUrlChange}
                placeholder="https://example.com/image.png"
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">Enter a direct URL to your image</p>
            </div>
          </TabsContent>
        </Tabs>

        {showPreview && (
          <div className="border rounded-lg p-4">
            <Label className="text-sm mb-2 block">Preview</Label>
            <img src={imageSrc} alt="Preview" className="max-w-full max-h-64 mx-auto rounded" onError={() => setImageSrc(null)} />
          </div>
        )}

        {showCropper && (
          <div className="space-y-3">
            <Label className="text-sm">Crop & position</Label>
            <div className="relative h-64 w-full rounded-lg overflow-hidden bg-muted">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={cropAspect}
                cropShape={cropShape}
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="flex items-center gap-3 px-1">
              <ZoomOut className="size-4 text-muted-foreground shrink-0" />
              <Slider value={[zoom]} min={1} max={3} step={0.05} onValueChange={([v]) => setZoom(v)} className="flex-1" />
              <ZoomIn className="size-4 text-muted-foreground shrink-0" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={!imageSrc}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
